import type { Client } from '~/types/user';

/**
 * Composable for checking client access based on shop owner types
 * This centralizes the logic for determining what features a client has access to
 */
export const useClientAccess = () => {
  /**
   * Check if a client has OMS shop owners
   */
  const hasOMSAccess = (client: Client | null): boolean => {
    if (!client || !client.shop_owners) {
      return false;
    }
    return client.shop_owners.some(
      shopOwner => shopOwner.third_party === 'OMS'
    );
  };

  /**
   * Check if a client has CMS shop owners
   */
  const hasCMSAccess = (client: Client | null): boolean => {
    if (!client || !client.shop_owners) {
      return false;
    }
    return client.shop_owners.some(
      shopOwner => shopOwner.third_party === 'CMS'
    );
  };

  /**
   * Check if a client has access to a specific route based on shop owner types
   */
  const hasRouteAccess = (
    client: Client | null,
    routePath: string
  ): boolean => {
    if (!client || !client.shop_owners) {
      return false;
    }

    // Customer Care routes require CMS shop owners
    if (routePath.startsWith('/customer-care')) {
      return hasCMSAccess(client);
    }

    // Fulfilment routes require OMS shop owners
    if (routePath.startsWith('/fulfilment')) {
      return hasOMSAccess(client);
    }

    // Other routes are generally accessible
    return true;
  };

  /**
   * Where to land for the given client.
   *
   * Only a CMS-only client gets a different page; everything else goes to the
   * fulfilment overview, including a client with no shop owners at all. That
   * page reports its own error, which beats a dead end claiming the user has no
   * access when the real gap is the client's ShopCtrl configuration.
   */
  const getDefaultRoute = (client: Client | null): string => {
    if (hasCMSAccess(client) && !hasOMSAccess(client)) {
      return '/customer-care/kpi-overview';
    }

    return '/fulfilment/overview';
  };

  return {
    hasOMSAccess,
    hasCMSAccess,
    hasRouteAccess,
    getDefaultRoute,
  };
};
